/**
 * Generic retry helper with exponential backoff.
 *
 * Runs `fn` up to `maxAttempts` times, waiting `initialDelayMs * 2^attempt`
 * between attempts. Retries when `fn` throws or when `shouldRetry(result)`
 * returns true; otherwise returns the last result.
 */

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export type RetryWithBackoffOptions<T> = {
	maxAttempts?: number;
	initialDelayMs?: number;
	shouldRetry?: (result: T) => boolean;
};

export async function retryWithBackoff<T>(
	fn: () => Promise<T>,
	options: RetryWithBackoffOptions<T> = {},
): Promise<T> {
	const { maxAttempts = 3, initialDelayMs = 500, shouldRetry } = options;
	let lastError: unknown;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		try {
			const result = await fn();
			if (!shouldRetry || !shouldRetry(result)) return result;
			if (attempt === maxAttempts - 1) return result;
		} catch (err) {
			lastError = err;
			if (attempt === maxAttempts - 1) throw err;
		}
		await sleep(initialDelayMs * Math.pow(2, attempt));
	}

	throw lastError;
}
